import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';

import { enter } from 'src/app/resources/animations';
import { Log } from 'src/app/utils/log';
import { RootState } from 'src/app/reducers';
import { dispatchInfoMessage } from 'src/app/actions/core.actions';
import { CreateBookmarkProblemListRequest, Visibility, VisibilityType } from 'src/app/models/create-bookmark-problem-list';
import { createBookmarkProblemList, initializeCreateBookmarkProblemListState } from 'src/app/actions/bookmark-problem-list.actions';
import { getCreateBookmarkProblemListResult } from 'src/app/selectors/bookmark-problem-list.selectors';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface AddBookmarkProblemListDialogData {
  subjectId: string;
}

@Component({
  selector: 'app-add-bookmark-problem-list-dialog',
  templateUrl: './add-bookmark-problem-list-dialog.component.html',
  styleUrls: ['./add-bookmark-problem-list-dialog.component.scss'],
  animations: [enter]
})
export class AddBookmarkProblemListDialogComponent implements OnInit, OnDestroy {
  addBookmarkProblemListForm: UntypedFormGroup;
  dialogTitle: string;
  bookmarkProblemListCreating = false;
  errorMessage = '';
  private LOG_SOURCE = this.constructor.name;

  protected readonly VisibilityType = VisibilityType;

  constructor(
    private store: Store<RootState>,
    private dialogRef: MatDialogRef<AddBookmarkProblemListDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddBookmarkProblemListDialogData
  ) {}

  ngOnInit() {
    this.dialogTitle = '新規リスト作成';

    this.setUpForms();
  }

  ngOnDestroy() {
    this.store.dispatch(initializeCreateBookmarkProblemListState());
  }

  createBookmarkProblemList() {
    if (this.addBookmarkProblemListForm.invalid) {
      this.addBookmarkProblemListForm.markAllAsTouched();
      return;
    }

    if (!this.data.subjectId) {
      Log.warn(this.LOG_SOURCE, `dialog.data(教科ID)が設定されていません: `, this.data.subjectId);
      return;
    }

    Log.debug(this.LOG_SOURCE, 'お気に入りリストを作成します');
    this.disableForms();
    this.errorMessage = '';

    const name: string = this.addBookmarkProblemListForm.get('listName').value;
    const visibility: Visibility = this.addBookmarkProblemListForm.get('visibility').value;
    const request: CreateBookmarkProblemListRequest = {
      subjectId: this.data.subjectId,
      name,
      visibility
    };

    this.store.dispatch(createBookmarkProblemList(request));

    this.store
      .select(getCreateBookmarkProblemListResult)
      .pipe(
        filter(it => it != null),
        take(1)
      )
      .subscribe(result => {
        this.store.dispatch(initializeCreateBookmarkProblemListState());
        Log.debug(this.LOG_SOURCE, `お気に入りリストの作成結果: `, result);
        if (result.success) {
          const message = `${name} を登録しました`;
          this.dialogRef.close(true);
          this.store.dispatch(
            dispatchInfoMessage({
              message
            })
          );
          return;
        }

        Log.warn(this.LOG_SOURCE, `お気に入りリストの作成エラー: err.code: ${result.error ? result.error.code : 'none'}`, result.error);
        this.enableForms();
        this.errorMessage = result.error ? `[${result.error.code}] ${result.error.message}` : 'エラーが発生しました';
      });
  }

  cancel() {
    this.dialogRef.close(false);
  }

  // set ups or private method ---------------------------------------------------------------
  private setUpForms() {
    this.addBookmarkProblemListForm = new FormGroup({
      listName: new FormControl('', [Validators.required, Validators.maxLength(50)]),
      visibility: new FormControl(VisibilityType.PRIVATE)
    });
  }

  private disableForms() {
    Object.keys(this.addBookmarkProblemListForm.controls).forEach(ctrlName => {
      this.addBookmarkProblemListForm.get(ctrlName).disable();
    });
    this.bookmarkProblemListCreating = true;
  }

  private enableForms() {
    Object.keys(this.addBookmarkProblemListForm.controls).forEach(ctrlName => {
      this.addBookmarkProblemListForm.get(ctrlName).enable();
    });
    this.bookmarkProblemListCreating = false;
  }
}
